/* eslint-disable */
// Keep function as-is so it's easier to get upstream changes.
import { createHash } from 'node:crypto';
import type { BinaryLike, BinaryToTextEncoding } from 'node:crypto';
import { makeLegalIdentifier } from '@rollup/pluginutils';
import type { ResolvedConfig } from 'vite';
import { parseAst } from 'vite';
import type { Identifier, Literal } from 'estree';
import colors from 'picocolors';
import { interopHelper } from './utils.js';

interface ImportNameSpecifier {
  importedName: string;
  localName: string;
}

// https://github.com/vitejs/vite/blob/a50b21b2c65595204ac503f18e5a8f04b18cb91b/packages/vite/src/node/plugins/importAnalysis.ts#L976
export function transformCjsImport(
  importExp: string,
  url: string,
  rawUrl: string,
  importIndex: number,
  importer: string,
  config: ResolvedConfig
): string | undefined {
  const node = parseAst(importExp).body[0];

  // `export * from '...'` may cause unexpected problem, so give it a warning
  if (
    config.command === 'serve' &&
    node?.type === 'ExportAllDeclaration' &&
    !node.exported
  ) {
    config.logger.warn(
      colors.yellow(
        `\nUnable to interop \`${importExp}\` in ${importer}, this may lose module exports. Please export "${rawUrl}" as ESM or use named exports instead, e.g. \`export { A, B } from "${rawUrl}"\``
      )
    );
  } else if (
    node?.type === 'ImportDeclaration' ||
    node?.type === 'ExportNamedDeclaration'
  ) {
    if (!node.specifiers.length) {
      return `import "${url}"`;
    }

    const importNames: ImportNameSpecifier[] = [];
    const exportNames: string[] = [];
    let defaultExports: string = '';
    for (const spec of node.specifiers) {
      if (spec.type === 'ImportSpecifier') {
        const importedName = getIdentifierNameOrLiteralValue(
          spec.imported
        ) as string;
        const localName = spec.local.name;
        importNames.push({ importedName, localName });
      } else if (spec.type === 'ImportDefaultSpecifier') {
        importNames.push({
          importedName: 'default',
          localName: spec.local.name,
        });
      } else if (spec.type === 'ImportNamespaceSpecifier') {
        importNames.push({ importedName: '*', localName: spec.local.name });
      } else if (spec.type === 'ExportSpecifier') {
        // for ExportSpecifier, local name is same as imported name
        // prefix the variable name to avoid clashing with other local variables
        const importedName = getIdentifierNameOrLiteralValue(
          spec.local
        ) as string;
        // we want to specify exported name as variable and re-export it
        const exportedName = getIdentifierNameOrLiteralValue(
          spec.exported
        ) as string;
        if (exportedName === 'default') {
          defaultExports = makeLegalIdentifier(
            `__vite__cjsExportDefault_${importIndex}`
          );
          importNames.push({ importedName, localName: defaultExports });
        } else {
          const localName = `__vite__cjsExport${
            spec.exported.type === 'Literal'
              ? `L_${getHashFromString(spec.exported.value as string)}`
              : 'I_' + spec.exported.name
          }`;
          importNames.push({ importedName, localName });
          exportNames.push(
            `${localName} as ${spec.exported.type === 'Literal' ? JSON.stringify(exportedName) : exportedName}`
          );
        }
      }
    }

    // If there is multiple import for same id in one file,
    // importIndex will prevent the cjsModuleName to be duplicate
    const cjsModuleName = makeLegalIdentifier(
      `__vite__cjsImport${importIndex}_${rawUrl}`
    );
    const lines: string[] = [`import ${cjsModuleName} from "${url}"`];
    importNames.forEach(({ importedName, localName }) => {
      if (importedName === '*') {
        lines.push(
          `const ${localName} = (${interopHelper.toString()})(${cjsModuleName})`
        );
      } else if (importedName === 'default') {
        lines.push(
          `const ${localName} = ${cjsModuleName}.__esModule ? ${cjsModuleName}.default : ${cjsModuleName}`
        );
      } else {
        lines.push(`const ${localName} = ${cjsModuleName}["${importedName}"]`);
      }
    });
    if (defaultExports) {
      lines.push(`export default ${defaultExports}`);
    }
    if (exportNames.length) {
      lines.push(`export { ${exportNames.join(', ')} }`);
    }

    return lines.join('; ');
  }
}

const getIdentifierNameOrLiteralValue = (node: Identifier | Literal) =>
  node?.type === 'Identifier' ? node?.name : node?.value;

const hash = (
  algorithm: string,
  data: BinaryLike,
  outputEncoding: BinaryToTextEncoding
) => createHash(algorithm).update(data).digest(outputEncoding);

const getHashFromString = (text: string, length = 8): string => {
  const h = hash('sha256', text, 'hex').substring(0, length);
  if (length <= 64) return h;
  return h.padEnd(length, '_');
};
